import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  IconButton,
  Chip,
  Stack,
  Divider,
  Paper,
  Avatar,
  Tooltip,
  LinearProgress,
  Badge,
  Button,
} from '@mui/material';
import {
  Close as CloseIcon,
  ArrowForward as ArrowForwardIcon,
  QrCode as QrCodeIcon,
  Engineering as EngineeringIcon,
  Build as BuildIcon,
  Palette as CoatingIcon,
  CheckCircle as CompletedIcon,
  Schedule as InProgressIcon,
  AccessTime as PendingIcon,
  Person as PersonIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';
import { formatDate, formatCurrency, formatStatusText } from '../../utils/formatters';
import OrderTimeline from './OrderTimeline';
import { getProductDisplayName } from '../../utils/productUtils';
import ItemQRCodeGenerator from '../orders/ItemQRCodeGenerator';

interface FrameItem {
  id: string;
  qr_code: string;
  type: string;
  current_stage: string;
  current_stage_order: number;
  status: string;
  assigned_to: string;
  supervisor_id: string | null;
  started_at: string | null;
  completed_at: string | null;
  estimated_hours: number | null;
  actual_hours: number | null;
  quantity: number;
  logs: any[];
}

interface Product {
  id: string;
  name: string;
  type: string;
  description: string;
  total_items: number;
  completed_items: number;
  unit_price: number | null;
  quantity: number;
  specifications: Record<string, any>;
  frame_items: FrameItem[];
  door_items: FrameItem[];
  net_items: FrameItem[];
  openable_type_items: FrameItem[];
  panel_items: FrameItem[];
  panel_window_items: FrameItem[];
}

interface OrderDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  order: {
    id: string;
    orderNumber?: string;
    orderNumberNew?: string;
    customer: {
      name: string;
      phone: string;
      email?: string;
      address: string;
    };
    status: string;
    priority: string;
    totalAmount: number;
    paymentStatus: string;
    createdAt: string;
    products: Product[];
  };
}

type ViewState = 'products' | 'items' | 'timeline';

const OrderDetailsModal: React.FC<OrderDetailsModalProps> = ({
  isOpen,
  onClose,
  order,
}) => {
  const [currentView, setCurrentView] = useState<ViewState>('products');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedItem, setSelectedItem] = useState<FrameItem | null>(null);

  const handleProductSelect = (product: Product) => {
    setSelectedProduct(product);
    setCurrentView('items');
  };

  const handleItemSelect = (item: FrameItem) => {
    setSelectedItem(item);
    setCurrentView('timeline');
  };

  const handleBack = () => {
    if (currentView === 'timeline') {
      setCurrentView('items');
      setSelectedItem(null);
    } else if (currentView === 'items') {
      setCurrentView('products');
      setSelectedProduct(null);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return <CompletedIcon fontSize="small" />;
      case 'in_progress':
        return <InProgressIcon fontSize="small" />;
      case 'pending':
        return <PendingIcon fontSize="small" />;
      default:
        return <PendingIcon fontSize="small" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'primary';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getItemTypeIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'frame':
        return <BuildIcon />;
      case 'door':
        return <EngineeringIcon />;
      case 'panel':
        return <BuildIcon />;
      case 'panel_window':
        return <EngineeringIcon />;
      case 'net':
        return <BuildIcon />;
      case 'openable_type':
        return <EngineeringIcon />;
      default:
        return <BuildIcon />;
    }
  };

  const getAllItems = (product: Product): FrameItem[] => {
    return [
      ...(product.frame_items || []),
      ...(product.door_items || []),
      ...(product.net_items || []),
      ...(product.openable_type_items || []),
      ...(product.panel_items || []),
      ...(product.panel_window_items || []),
    ];
  };

  const getItemTypeCounts = (product: Product) => {
    return {
      frame: product.frame_items?.length || 0,
      door: product.door_items?.length || 0,
      panel: product.panel_items?.length || 0,
      panelWindow: product.panel_window_items?.length || 0,
      net: product.net_items?.length || 0,
      openableType: product.openable_type_items?.length || 0,
    };
  };

  const getCompletedCounts = (product: Product) => {
    return {
      frame: product.frame_items?.filter(item => item.status === 'completed').length || 0,
      door: product.door_items?.filter(item => item.status === 'completed').length || 0,
      panel: product.panel_items?.filter(item => item.status === 'completed').length || 0,
      panelWindow: product.panel_window_items?.filter(item => item.status === 'completed').length || 0,
      net: product.net_items?.filter(item => item.status === 'completed').length || 0,
      openableType: product.openable_type_items?.filter(item => item.status === 'completed').length || 0,
    };
  };

  // Generate timeline events based on actual manufacturing workflow stages
  const generateTimelineEvents = (item: FrameItem) => {
    // Get the appropriate stages for the item type
    const getStagesForItemType = (itemType: string) => {
      const stageMap: Record<string, string[]> = {
        'Frame': ['Fabrication', 'Coating', 'Dispatch'],
        'Panel': [
          'Cutting', 'Assembly', 'Hinges', 'Member', 'Bar', 'Full Welding',
          'Grinding', 'Leveling', 'Finishing', 'Khacha', 'Coating',
          'Accessories', 'Glass Fitting', 'Dispatch'
        ],
        'Panel Window': ['Fabrication', 'Coating', 'Fitting in Panel'],
        'Net Fixing': ['Frame Cut', 'Weld', 'Net Fixing'],
        'Openable Type': ['Design', 'Fabrication', 'Assembly', 'Coating', 'Dispatch'],
      };
      return stageMap[itemType] || ['Fabrication', 'Assembly', 'Dispatch'];
    };

    const stages = getStagesForItemType(item.type || 'Frame');
    
    // Create timeline events based on actual workflow stages
    const events = stages.map((stage, index) => {
      let status: 'completed' | 'in_progress' | 'pending' | 'skipped' = 'pending';
      
      // Find the current stage index in the stages array
      const currentStageIndex = stages.indexOf(item.current_stage || '');
      
      // Determine status based on sequential progression
      if (index < currentStageIndex) {
        // All stages before the current stage are completed
        status = 'completed';
      } else if (index === currentStageIndex) {
        // Current stage takes the item's status
        status = item.status as any || 'in_progress';
      } else {
        // All stages after the current stage are pending
        status = 'pending';
      }

      return {
        id: `stage-${index}`,
        stage: stage,
        status: status,
        user_id: item.assigned_to || 'system',
        user_name: item.assigned_to ? `Worker ${item.assigned_to.slice(-4)}` : 'System',
        timestamp: item.started_at || new Date().toISOString(),
        notes: `Stage: ${stage}`,
        stage_order: index,
        qr_code: item.qr_code,
        duration_hours: item.actual_hours || undefined,
      };
    });

    return events;
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bgcolor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1299,
          opacity: isOpen ? 1 : 0,
          visibility: isOpen ? 'visible' : 'hidden',
          transition: 'all 0.3s ease-in-out',
        }}
        onClick={onClose}
      />

      {/* Products Panel - Shifts left when other panels appear */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          right: selectedProduct ? (selectedItem ? '60%' : '30%') : '0',
          height: '100vh',
          width: '30%',
          bgcolor: 'background.paper',
          boxShadow: '-4px 0 20px rgba(0,0,0,0.1)',
          zIndex: 1300,
          display: 'flex',
          flexDirection: 'column',
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          borderLeft: '1px solid',
          borderColor: 'divider',
        }}
      >
        {/* Products Panel Header */}
        <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Products
            </Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Order: {order.orderNumberNew || order.orderNumber || order.order_tracking_code || order.id.slice(-8)}
            </Typography>
            <Chip label={formatStatusText(order.status)} color="primary" size="small" />
            <Chip label={order.priority} color="warning" size="small" />
          </Stack>
        </Box>

        {/* Products Panel Content */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Products ({order.products.length})
            </Typography>
            <Stack spacing={2}>
              {order.products.map((product) => {
                const itemCounts = getItemTypeCounts(product);
                const completedCounts = getCompletedCounts(product);
                const totalItems = Object.values(itemCounts).reduce((sum, count) => sum + count, 0);
                const totalCompleted = Object.values(completedCounts).reduce((sum, count) => sum + count, 0);
                const progress = totalItems > 0 ? (totalCompleted / totalItems) * 100 : 0;

                return (
                  <Card key={product.id} variant="outlined">
                    <ListItemButton onClick={() => handleProductSelect(product)}>
                      <CardContent sx={{ width: '100%', p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {product.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {product.type} • {product.description}
                            </Typography>
                          </Box>
                          <ArrowForwardIcon color="action" />
                        </Box>
                        
                        <Box sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="body2">
                              Progress: {totalCompleted}/{totalItems} items
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {Math.round(progress)}% Complete
                            </Typography>
                          </Box>
                          <LinearProgress 
                            variant="determinate" 
                            value={progress} 
                            sx={{ height: 6, borderRadius: 3 }}
                          />
                        </Box>

                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {itemCounts.frame > 0 && (
                            <Chip
                              label={`Frame: ${completedCounts.frame}/${itemCounts.frame}`}
                              size="small"
                              variant="outlined"
                              color="primary"
                              icon={getItemTypeIcon('frame')}
                            />
                          )}
                          {itemCounts.door > 0 && (
                            <Chip
                              label={`Door: ${completedCounts.door}/${itemCounts.door}`}
                              size="small"
                              variant="outlined"
                              color="secondary"
                              icon={getItemTypeIcon('door')}
                            />
                          )}
                          {itemCounts.panel > 0 && (
                            <Chip
                              label={`Panel: ${completedCounts.panel}/${itemCounts.panel}`}
                              size="small"
                              variant="outlined"
                              color="info"
                              icon={getItemTypeIcon('panel')}
                            />
                          )}
                          {itemCounts.panelWindow > 0 && (
                            <Chip
                              label={`Panel Window: ${completedCounts.panelWindow}/${itemCounts.panelWindow}`}
                              size="small"
                              variant="outlined"
                              color="success"
                              icon={getItemTypeIcon('panel_window')}
                            />
                          )}
                          {itemCounts.net > 0 && (
                            <Chip
                              label={`Net: ${completedCounts.net}/${itemCounts.net}`}
                              size="small"
                              variant="outlined"
                              color="warning"
                              icon={getItemTypeIcon('net')}
                            />
                          )}
                          {itemCounts.openableType > 0 && (
                            <Chip
                              label={`Openable: ${completedCounts.openableType}/${itemCounts.openableType}`}
                              size="small"
                              variant="outlined"
                              color="error"
                              icon={getItemTypeIcon('openable_type')}
                            />
                          )}
                        </Stack>
                      </CardContent>
                    </ListItemButton>
                  </Card>
                );
              })}
            </Stack>
          </Box>
        </Box>
      </Box>

      {/* Items Panel - Shifts left when timeline panel appears */}
      {selectedProduct && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            right: selectedItem ? '30%' : '0',
            height: '100vh',
            width: '30%',
            bgcolor: 'background.paper',
            boxShadow: '-4px 0 20px rgba(0,0,0,0.1)',
            zIndex: 1301,
            display: 'flex',
            flexDirection: 'column',
            transform: currentView === 'items' || currentView === 'timeline' ? 'translateX(0)' : 'translateX(100%)',
            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            borderLeft: '1px solid',
            borderColor: 'divider',
          }}
        >
          {/* Items Panel Header */}
          <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {selectedProduct.name} - Items
              </Typography>
              <Button onClick={handleBack} size="small" startIcon={<ArrowForwardIcon sx={{ transform: 'rotate(180deg)' }} />}>
                Back
              </Button>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {selectedProduct.type} • {getAllItems(selectedProduct).length} items
            </Typography>
          </Box>

          {/* Items Panel Content */}
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            <List>
              {getAllItems(selectedProduct).map((item, index) => (
                <React.Fragment key={item.id}>
                  <ListItem
                    sx={{ px: 0 }}
                    secondaryAction={
                      <IconButton onClick={() => handleItemSelect(item)}>
                        <TimelineIcon />
                      </IconButton>
                    }
                  >
                    <ListItemButton onClick={() => handleItemSelect(item)}>
                      <ListItemIcon>
                        <Avatar sx={{ bgcolor: getStatusColor(item.status) + '.main' }}>
                          {getItemTypeIcon(item.type)}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle1">
                              {item.type} Item
                            </Typography>
                            <Chip
                              label={formatStatusText(item.status)}
                              color={getStatusColor(item.status) as any}
                              size="small"
                              icon={getStatusIcon(item.status)}
                            />
                            <Chip
                              label={item.current_stage}
                              size="small"
                              variant="outlined"
                            />
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              QR: {item.qr_code?.slice(-8) || 'N/A'} • Assigned: {item.assigned_to?.slice(-8) || 'N/A'}
                            </Typography>
                            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                              <ItemQRCodeGenerator
                                itemId={item.id}
                                orderId={order.id}
                                productId={selectedProduct.id}
                                itemType={item.type || 'item'}
                                itemName={item.number || item.id}
                                showQRCode={true}
                                existingQrCode={item.qr_code}
                                existingQrImageUrl={item.qr_code_image_url}
                              />
                            </Box>
                            {item.estimated_hours && (
                              <Typography variant="caption" color="text.secondary">
                                Est: {item.estimated_hours}h
                                {item.actual_hours && ` • Actual: ${item.actual_hours}h`}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                  {index < getAllItems(selectedProduct).length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Box>
        </Box>
      )}

      {/* Timeline Panel - Always at right edge when visible */}
      {selectedItem && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            right: '0',
            height: '100vh',
            width: '30%',
            bgcolor: 'background.paper',
            boxShadow: '-4px 0 20px rgba(0,0,0,0.1)',
            zIndex: 1302,
            display: 'flex',
            flexDirection: 'column',
            transform: currentView === 'timeline' ? 'translateX(0)' : 'translateX(100%)',
            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            borderLeft: '1px solid',
            borderColor: 'divider',
          }}
        >
          <OrderTimeline
            orderId={order.id}
            orderNumber={order.orderNumberNew || order.orderNumber || order.order_tracking_code || order.id.slice(-8)}
            itemId={selectedItem.id}
            itemType={selectedItem.type}
            events={generateTimelineEvents(selectedItem)}
            onClose={handleBack}
          />
        </Box>
      )}
    </>
  );
};

export default OrderDetailsModal;